import React, { useEffect, useState, useRef } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { toast } from 'react-toastify';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Input } from 'reactstrap';
import Icon from '@mdi/react';
import {
  mdiFeatureSearchOutline,
  mdiAlertCircleOutline,
  mdiEyeOutline,
} from '@mdi/js';
import Select from 'react-select';
import useExpenseTypes from '../../hooks/useExpenseTypes'; // adjust import
import { fetchReimburseRate } from '../../../store/apis/expenseService';
import ExpenseViewAttachmentIcon from '../../../assets/icons/expense-view-attachment.svg';
import ExpenseAddMoreIcon from '../../../assets/icons/expense-add-more.svg';
import ExpenseDeleteRowIcon from '../../../assets/icons/expense-delete-row.svg';
import { useExpenseClaimCtx } from '../../../pages/ExpenseClaims/context/ExpenseClaimProvider';
import { useExpenseClaimUtilCtx } from '../../../pages/ExpenseClaims/context/ExpenseClaimUtilProvider';
import AdditionalDetailsModal from '../AdditionalDetailsModal/AdditionalDetailsModal';
import { useConfirmationModal } from '../../hooks';
import ExpenseApproverIcon from '../../../assets/icons/expense-approval.svg';
import attachDocIcon from '../../../assets/icons/attachdocicon.png';

import {
  CLAIM_FORM_TYPES,
  getClaimApprovalColor,
  getClaimStatusColor,
} from '../../../pages/ExpenseClaims/Employee/EmployeeExpenseClaim/constants';
import {
  historyDataConfig,
  mainFieldKeys,
  approvalDataConfig,
} from './constants';
import { DatePicker } from '../../Common';

// Import SCSS
import './ExpenseClaimRow.scss';
import AmountInputProps from '../AmountInputProps/AmountInputProps';

function ExpenseClaimRow({
  index,
  item,
  remove,
  currencyLists,
  setSearchText,
  methods,
  watch,
  formType = null,
  handleAddRow,
}) {
  const queryClient = useQueryClient();
  const fileRef = useRef(null);
  const viewApproversRef = useRef(null);
  const {
    formMethods,
    resetRowModalState,
    expenseClaimHeaders,
    setViewAttachmentsModalOpen,
    setViewAttachmentSrNo,
    setIsModalOpen,
    setModalConfig,
    modeType,
    addRow,
    validateLastRow,
    setDisplayModalOpen,
    setDisplayModalFile,
  } = useExpenseClaimCtx();
  const {
    expenseClaimApprovalDetailsMutation,
    deleteExpenseClaimAttachmentListMutation,
    cancelExpenseClaimMutation,
    uploadAttachmentMutation,
    fetchAttachmentsMutation,
    downloadAttachmentMutation,
    defaultCurrCode,
    expenseCategories,
    fetchExpenseClaimDetailsListWithQueryMutation,
    reimbursateRateMutation,
  } = useExpenseClaimUtilCtx();
  const { control } = useFormContext();
  const [isAdditionalModalOpen, setIsAdditionalModalOpen] = useState(false);
  const { open, ConfirmationModal } = useConfirmationModal();
  const rowWatchValues = formMethods.watch(`claims.${index}`);
  const [isFocused, setIsFocused] = useState('');
  const [attachmentExists, setAttachmentExists] = useState(false);

  const expTypeRef = useRef(null);
  const expCategoryRef = useRef(null);
  // const exRateRef = useRef(null);
  const noFileMessageRef = useRef(false);

  const toggleModal = () => {
    setIsAdditionalModalOpen(prev => !prev);
  };

  const { data: rowExpenseTypes = [] } = useExpenseTypes(
    rowWatchValues?.expCategory,
  );

  const sortedCurrencies = [...(currencyLists || [])].sort((a, b) => {
    const aIsDefault = a.Code?.trim() === defaultCurrCode?.trim();
    const bIsDefault = b.Code?.trim() === defaultCurrCode?.trim();
    if (aIsDefault) return -1;
    if (bIsDefault) return 1;
    return 0;
  });

  const currencyOptions = sortedCurrencies.map(option => ({
    value: option.Code,
    label: `${option.Name} : ${option.Code}`,
  }));

  const { data: reimburseRate } = useQuery({
    queryKey: [
      'reimburseRate',
      rowWatchValues?.currency,
      defaultCurrCode,
      rowWatchValues?.expDate,
    ],
    enabled:
      !!rowWatchValues?.currency &&
      !!defaultCurrCode &&
      !!rowWatchValues?.expDate,
    queryFn: () =>
      fetchReimburseRate({
        fromCurr: rowWatchValues?.currency,
        toCurr: defaultCurrCode,
        expenseDate: rowWatchValues?.expDate,
      }),
  });

  useEffect(() => {
    if (
      rowWatchValues?.currency &&
      !rowWatchValues?.expRate &&
      defaultCurrCode &&
      reimburseRate
    ) {
      if (reimburseRate[0].Rate === null) {
        methods.setValue(`claims.${index}.expRate`, '1.000000', {
          shouldValidate: true,
        });
      } else {
        methods.setValue(
          `claims.${index}.expRate`,
          parseFloat(reimburseRate[0].Rate).toFixed(6),
        );
      }
    }
  }, [
    reimburseRate,
    rowWatchValues?.currency,
    rowWatchValues?.expRate,
    defaultCurrCode,
    methods,
    index,
  ]);

  const handleRowDelete = async () => {
    if (rowWatchValues?.srNo && expenseClaimHeaders?.claimNo) {
      await deleteExpenseClaimAttachmentListMutation.mutateAsync({
        docType: 'DOC_TYPE_EXPENSE_CLAIM',
        claimNo: expenseClaimHeaders?.claimNo,
        docIndex: rowWatchValues?.srNo,
      });
      await cancelExpenseClaimMutation.mutateAsync({
        srNo: rowWatchValues?.srNo,
        claimNo: expenseClaimHeaders?.claimNo,
        reason: '',
      });
      toast.success('Expense claim row deleted successfully.', {
        className: 'toast-success',
      });
      queryClient.invalidateQueries(['expenseClaimDetails']);
      queryClient.invalidateQueries(['expenseClaimDetailsHeader']);
    } else {
      remove(index);
    }
  };

  const handleFileUpload = async e => {
    // Multiple file upload handling
    const { files } = e.target;
    if (!files || files.length === 0) {
      toast.error('No file selected for upload.', {
        className: 'toast-error',
      });
      return;
    }
    if (!expenseClaimHeaders?.claimNo) {
      toast.error(
        'Claim number is not available. Please save the claim first.',
        {
          className: 'toast-error',
        },
      );
      return;
    }

    const formData = new FormData();
    formData.append('docType', 'DOC_TYPE_EXPENSE_CLAIM');
    formData.append('claimNo', expenseClaimHeaders?.claimNo || '');
    formData.append('docFileName', files[0].name);
    formData.append('docTypeCode', '');
    formData.append('comments', '');
    formData.append('docIndex', rowWatchValues?.srNo || index + 1);
    formData.append('finCoCd', expenseClaimHeaders?.finCoCd || '');
    // Upload File array
    Array.from(files).forEach(file => {
      formData.append('file', file);
    });
    await uploadAttachmentMutation.mutateAsync(formData);
    toast.success('File uploaded successfully.', {
      className: 'toast-success',
    });
    queryClient.invalidateQueries([
      'expenseClaimDetails',
      'expenseClaimDetailsHeader',
    ]);
    setAttachmentExists(true);
  };

  const handleFileView = async () => {
    if (!attachmentExists && !rowWatchValues?.srNo) {
      if (!noFileMessageRef.current) {
        toast.error('No file to view. Please upload a file first.');
        noFileMessageRef.current = true;
      }
      return;
    }
    // If file exists, reset the flag (optional)
    noFileMessageRef.current = false;

    if (!expenseClaimHeaders?.claimNo) {
      toast.error(
        'Claim number is not available. Please save the claim first.',
        {
          className: 'toast-error',
        },
      );
      return;
    }
    try {
      const rowAttachments = await fetchAttachmentsMutation.mutateAsync({
        docType: 'DOC_TYPE_EXPENSE_CLAIM',
        claimNo: expenseClaimHeaders?.claimNo || '',
        docIndex: rowWatchValues?.srNo || index + 1,
        finCoCd: expenseClaimHeaders?.finCoCd || '',
      });

      if (!rowAttachments || rowAttachments.length === 0) {
        toast.error('No attachments found for this row.');
        return;
      }

      if (rowAttachments.length === 1) {
        const attachmentBlob = await downloadAttachmentMutation.mutateAsync({
          type: 'Expense_Claim',
          fileName: rowAttachments?.[0]?.DocName,
          claimNo: expenseClaimHeaders?.claimNo || '',
          finCoCd: expenseClaimHeaders?.finCoCd || '',
        });
        const file = new File([attachmentBlob], rowAttachments?.[0]?.DocName, {
          type: attachmentBlob.type,
        });
        setDisplayModalFile(file);
        setDisplayModalOpen(true);
        return;
      }
      setViewAttachmentsModalOpen(true);
      setViewAttachmentSrNo(rowWatchValues?.srNo || index + 1);
    } catch {
      toast.error('Error fetching attachments.', {
        className: 'toast-error',
      });
    }
  };

  const handleExpenseApproverIconClick = async () => {
    if (!expenseClaimHeaders?.claimNo) {
      toast.error('Please save the claim before approving.', {
        className: 'toast-error',
      });
      return;
    }
    const expenseClaimApprovalResponse =
      await expenseClaimApprovalDetailsMutation.mutateAsync({
        listType: 'APPROVAL',
        claimNo: expenseClaimHeaders?.claimNo,
        appType: null,
        appRank: rowWatchValues?.srNo || index + 1,
      });
    const getExpenseCategory = expenseCategories?.find(
      category => category.Code === rowWatchValues?.expCategory,
    );
    const getExpenseType = rowExpenseTypes?.find(
      type => type.Code === rowWatchValues?.expType,
    );
    setModalConfig({
      title: `Approver Details (${getExpenseCategory?.Description || 'Select one'}-${getExpenseType?.Description || 'Select one'})`,
      data: expenseClaimApprovalResponse,
      dataConfig: approvalDataConfig,
      size: 'xl',
    });
    setIsModalOpen(true);
  };

  const handlePastHistoryClick = async () => {
    const historyResponse =
      await fetchExpenseClaimDetailsListWithQueryMutation.mutateAsync({
        claimNo: expenseClaimHeaders?.claimNo,
        queryType: 'APPROVAL',
        expenseCategory: rowWatchValues?.expCategory || '',
        expenseType: rowWatchValues?.expType || '',
        sorNo: rowWatchValues?.sorNo || '',
        employeeId: expenseClaimHeaders?.employeeId || '',
      });
    setModalConfig({
      title: 'Claims - History',
      data: historyResponse,
      dataConfig: historyDataConfig,
    });
    setIsModalOpen(true);
  };

  const claimStyle = () => {
    if (
      expenseClaimHeaders?.claimStatus &&
      formType === CLAIM_FORM_TYPES.VIEW &&
      modeType !== 'approve' &&
      modeType !== 'view'
    ) {
      return {
        backgroundColor: getClaimStatusColor(expenseClaimHeaders?.claimStatus),
      };
    }

    if (
      formType === CLAIM_FORM_TYPES.VIEW &&
      (modeType === 'approve' || modeType === 'view')
    ) {
      return {
        backgroundColor: '#f8f9fa',
      };
    }
    return {};
  };

  const {
    formState: { errors },
  } = formMethods;

  const checkErrorOpenModal = rowErrs => {
    if (!rowErrs) return;

    // check if any main field has an error
    const mainErrors = mainFieldKeys.some(key => Boolean(rowErrs[key]));
    // check if any *other* key has an error (i.e. your additional details)
    const additionalErrors = Object.keys(rowErrs)
      .filter(key => !mainFieldKeys.includes(key))
      .some(key => Boolean(rowErrs[key]));

    // if there are additionalErrors and no mainErrors, open the modal
    if (!mainErrors && additionalErrors && !isAdditionalModalOpen) {
      toggleModal();
    }
  };

  const checkLastRowAndAdd = async () => {
    // Check if this is the last row and if it has no errors
    const lastRowIndex = methods.getValues('claims').length - 1;
    if (index === lastRowIndex) {
      const testRes = await validateLastRow(false, false);
      if (!testRes?.isValid) {
        checkErrorOpenModal(testRes?.errors);
        return;
      }
      addRow();
    }
  };

  const computeReimburseAmt = (rate, amount) => {
    const reimburAmt = (amount * rate).toFixed(2);
    methods.setValue(`claims.${index}.reimburAmt`, reimburAmt);
  };

  async function updateRowRates(newCurr) {
    if (!defaultCurrCode || !rowWatchValues?.expDate || !newCurr) {
      return;
    }
    try {
      const response = await reimbursateRateMutation.mutateAsync({
        fromCurr: newCurr,
        toCurr: defaultCurrCode,
        expenseDate: rowWatchValues.expDate,
      });

      let rate = null;
      if (response?.[0]?.Rate) {
        rate = parseFloat(response?.[0]?.Rate).toFixed(6);
      } else {
        rate = parseFloat(1).toFixed(6);
      }
      methods.setValue(`claims.${index}.expRate`, rate);
      computeReimburseAmt(rate, methods.getValues(`claims.${index}.amount`));
    } catch (err) {
      /* empty */
    }
  }

  return (
    <>
      <tr
        key={item.id}
        style={{
          ...claimStyle(),
          borderBottom: '1px solid #98A2B3',
        }}
      >
        <td className="p-0 cell-border">
          <Controller
            control={control}
            name={`claims.${index}.expCategory`}
            render={({ field }) => (
              <Input
                {...field}
                type="select"
                className={`eec-input claim-row-input ${errors?.claims?.[index]?.expCategory ? 'is-invalid' : ''} ${isFocused === 'category' ? 'custom-focus' : ''}`}
                onChange={e => {
                  field.onChange(e);
                  methods.setValue(`claims.${index}.expType`, '');
                  resetRowModalState(index);
                }}
                // innerRef={expCategoryRef}
                innerRef={el => {
                  field.ref(el);
                  expCategoryRef.current = el;
                }}
                disabled={formType === CLAIM_FORM_TYPES.VIEW}
                title="Select the main category of the expense"
                onFocus={() => setIsFocused('category')}
                onBlur={() => setIsFocused('')}
              >
                <option value="">Category</option>
                {expenseCategories?.map(option => (
                  <option key={option?.Code} value={option?.Code}>
                    {option?.Description}
                  </option>
                ))}
              </Input>
            )}
          />
        </td>
        <td className="p-0 cell-border">
          <Controller
            control={control}
            name={`claims.${index}.expType`}
            render={({ field }) => (
              <Input
                {...field}
                type="select"
                className={`eec-input claim-row-input ${errors?.claims?.[index]?.expType ? 'is-invalid' : ''} ${isFocused === 'claimType' ? 'custom-focus' : ''}`}
                onChange={e => {
                  field.onChange(e.target.value);
                  resetRowModalState(index);
                }}
                /* innerRef={expTypeRef} */
                innerRef={el => {
                  field.ref(el);
                  expTypeRef.current = el;
                }}
                disabled={formType === CLAIM_FORM_TYPES.VIEW}
                title="Select the specific type of expense under the chosen category."
                onFocus={() => setIsFocused('claimType')}
                onBlur={() => setIsFocused('')}
              >
                <option value="">Type</option>
                {rowExpenseTypes.map(type => (
                  <option key={type.Code} value={type.Code}>
                    {type.Description}
                  </option>
                ))}
              </Input>
            )}
          />
        </td>
        <td className="p-0 cell-border">
          <Controller
            control={control}
            name={`claims.${index}.expDate`}
            render={({ field }) => (
              <DatePicker
                {...field}
                disabled={formType === CLAIM_FORM_TYPES.VIEW}
                className={`eec-input pe-0 claim-row-input ${errors?.claims?.[index]?.expDate ? 'is-invalid' : ''}`}
                error={!!errors?.claims?.[index]?.expDate}
                title="Enter the date mentioned on the bill/receipt."
                showIcon={false}
                forceTransparentDisabled
                calendarFocus={false}
              />
            )}
          />
        </td>
        <td className="p-0 cell-border">
          <Controller
            control={control}
            name={`claims.${index}.currency`}
            render={({ field }) => (
              <div
                className="select-with-error"
                title="Choose the currency in which the expense was paid."
              >
                <Select
                  {...field}
                  options={currencyOptions}
                  isDisabled={formType === CLAIM_FORM_TYPES.VIEW}
                  classNamePrefix="rs"
                  className="react-select-no-border"
                  value={{
                    label: rowWatchValues?.currency,
                    value: rowWatchValues?.currency,
                  }}
                  onChange={selected => {
                    field.onChange(selected?.value);
                    updateRowRates(selected?.value);
                  }}
                  onInputChange={inputValue => {
                    setSearchText(inputValue);
                    return inputValue;
                  }}
                  isSearchable
                  title="Choose the currency in which the expense was paid."
                  styles={{
                    control: (base, { isDisabled }) => ({
                      ...base,
                      backgroundColor: 'transparent',
                      border: 'none',
                      boxShadow: 'none',
                      cursor: isDisabled ? 'not-allowed' : base.cursor,
                      '&:hover': {
                        backgroundColor: isDisabled
                          ? 'transparent'
                          : base.backgroundColor,
                      },
                      width: '100%',
                    }),
                    singleValue: (base, { isDisabled }) => ({
                      ...base,
                      color: isDisabled ? '#6c757d' : base.color,
                    }),
                    placeholder: (base, { isDisabled }) => ({
                      ...base,
                      color: isDisabled ? '#6c757d' : base.color,
                    }),
                    menuPortal: base => ({
                      ...base,
                      zIndex: 9999,
                      width: '180px',
                      minWidth: '180px',
                    }),
                    valueContainer: base => ({
                      ...base,
                      cursor: 'text',
                    }),
                    dropdownIndicator: () => ({ display: 'none' }),
                    indicatorSeparator: () => ({ display: 'none' }),
                  }}
                  menuPortalTarget={
                    typeof document !== 'undefined' ? document.body : null
                  }
                  menuPosition="fixed"
                  noOptionsMessage={() => null}
                />
                {errors?.claims?.[index]?.currency && (
                  <div className="select-with-error__icon">
                    <Icon path={mdiAlertCircleOutline} size={0.75} />
                  </div>
                )}
              </div>
            )}
          />
        </td>
        <td className="p-0 cell-border">
          <Controller
            control={control}
            name={`claims.${index}.expRate`}
            render={({ field }) => (
              <Input
                {...field}
                className={`eec-input claim-row-input ${errors?.claims?.[index]?.expRate ? 'is-invalid' : ''}`}
                type="text"
                value={field.value || ''}
                disabled={
                  rowWatchValues?.currency?.trim() ===
                    defaultCurrCode?.trim() ||
                  formType === CLAIM_FORM_TYPES.VIEW
                }
                title="Enter the exchange rate if the expense was paid in a foreign currency. It is auto-filled based on currency."
                style={{
                  color:
                    rowWatchValues?.currency?.trim() === defaultCurrCode?.trim()
                      ? '#6c757d'
                      : '#000',
                }}
                onChange={e => {
                  field.onChange(e.target.value);
                  computeReimburseAmt(e.target.value, rowWatchValues?.amount);
                }}
                onBlur={e => {
                  // format only on blur
                  if (e.target.value !== '') {
                    const formatted = parseFloat(e.target.value).toFixed(6);
                    field.onChange(formatted);
                  }
                }}
              />
            )}
          />
        </td>
        <td className="p-0 cell-border">
          <Controller
            control={control}
            name={`claims.${index}.amount`}
            render={({ field }) => (
              <AmountInputProps
                value={field.value}
                onChange={e => {
                  field.onChange(e);
                  computeReimburseAmt(rowWatchValues?.expRate, e);
                }}
                disabled={formType === CLAIM_FORM_TYPES.VIEW}
                error={!!errors?.claims?.[index]?.amount}
              />
            )}
          />
        </td>
        <td className="p-0 cell-border">
          <Controller
            control={control}
            name={`claims.${index}.taxAmount`}
            render={({ field }) => (
              <Input
                {...field}
                className={`eec-input claim-row-input ${errors?.claims?.[index]?.taxAmount ? 'is-invalid' : ''}`}
                placeholder="0"
                type="number"
                disabled={formType === CLAIM_FORM_TYPES.VIEW}
                title="Enter VAT amount included in the expense (Only the VAT amount)."
              />
            )}
          />
        </td>
        <td className="p-0 cell-border">
          <Controller
            control={control}
            name={`claims.${index}.remarks`}
            render={({ field }) => (
              <Input
                {...field}
                className={`eec-input claim-row-input ${errors?.claims?.[index]?.remarks ? 'is-invalid' : ''}`}
                placeholder="Enter Remarks"
                disabled={formType === CLAIM_FORM_TYPES.VIEW}
                title="Add any comments or explanation related to the expense."
                onBlur={async e => {
                  if (e.target.value) {
                    checkLastRowAndAdd();
                  }
                }}
              />
            )}
          />
        </td>
        {/* <td className="align-middle text-center cell-border">
          <div className="d-flex flex-row gap-2 justify-content-center align-items-center">
            {modeType !== 'approve' &&
              modeType !== 'view' &&
              (rowWatchValues?.count && rowWatchValues?.count > 0 ? (
                <button
                  type="button"
                  className="btn btn-link p-0 me-1"
                  title="Click to view the attachment for this expense item."
                  onClick={() => {
                    if (!expenseClaimHeaders?.claimNo) {
                      toast.error(
                        'Please save the claim before adding attachments.',
                        {
                          className: 'toast-error',
                        },
                      );
                      return;
                    }
                    fileRef.current?.click();
                  }}
                  style={{
                    pointerEvents:
                      formType !== CLAIM_FORM_TYPES.VIEW ? 'auto' : 'none',
                  }}
                >
                  <img
                    src={ExpenseViewAttachmentIcon}
                    alt="Additional Item"
                    className="eec-row-icon"
                    style={{
                      cursor:
                        expenseClaimHeaders?.claimNo &&
                        formType !== CLAIM_FORM_TYPES.VIEW
                          ? 'pointer'
                          : 'not-allowed',
                      opacity:
                        expenseClaimHeaders?.claimNo &&
                        formType !== CLAIM_FORM_TYPES.VIEW
                          ? 1
                          : 0.5,
                    }}
                  />
                </button>
              ) : (
                <button
                  type="button"
                  className="btn btn-link p-0"
                  title="No attachments available for this expense item."
                  onClick={() => {
                    if (!expenseClaimHeaders?.claimNo) {
                      toast.error(
                        'Please save the claim before adding attachments.',
                        {
                          className: 'toast-error',
                        },
                      );
                      return;
                    }
                    fileRef.current?.click();
                  }}
                  disabled={!rowWatchValues?.srNo}
                >
                  <img src={attachDocIcon} alt="Attach" />
                </button>
              ))}
            <button
              type="button"
              className="btn btn-link p-0"
              title="Click to download the attachment"
              onClick={handleFileView}
            >
              <Icon
                path={mdiEyeOutline}
                size={0.7}
                style={{
                  cursor:
                    rowWatchValues?.count && rowWatchValues?.count > 0
                      ? 'pointer'
                      : 'not-allowed',
                  color:
                    rowWatchValues?.count && rowWatchValues?.count > 0
                      ? '#079455'
                      : getClaimApprovalColor(expenseClaimHeaders?.claimStatus),
                  opacity:
                    rowWatchValues?.count && rowWatchValues?.count > 0
                      ? 1
                      : 0.5,
                }}
              />
            </button>
          </div>
        </td> */}
        <td className="align-middle text-center cell-border">
          <div className="d-flex flex-row gap-2 justify-content-center align-items-center">
            {modeType !== 'approve' &&
              modeType !== 'view' &&
              (rowWatchValues?.count && rowWatchValues?.count > 0 ? (
                <button
                  type="button"
                  className="btn btn-link p-0 me-1"
                  title="Click to view the attachment for this expense item."
                  onClick={() => {
                    if (!expenseClaimHeaders?.claimNo) {
                      toast.error(
                        'Please save the claim before adding attachments.',
                        {
                          className: 'toast-error',
                        },
                      );
                      return;
                    }
                    fileRef.current?.click();
                  }}
                  style={{
                    pointerEvents:
                      formType !== CLAIM_FORM_TYPES.VIEW ? 'auto' : 'none',
                  }}
                >
                  <img
                    src={ExpenseViewAttachmentIcon}
                    alt="Additional Item"
                    className="eec-row-icon"
                    style={{
                      cursor:
                        expenseClaimHeaders?.claimNo &&
                        formType !== CLAIM_FORM_TYPES.VIEW
                          ? 'pointer'
                          : 'not-allowed',
                      opacity:
                        expenseClaimHeaders?.claimNo &&
                        formType !== CLAIM_FORM_TYPES.VIEW
                          ? 1
                          : 0.5,
                    }}
                  />
                </button>
              ) : (
                <button
                  type="button"
                  className="btn btn-link p-0"
                  title="No attachments available for this expense item."
                  onClick={() => {
                    if (!expenseClaimHeaders?.claimNo) {
                      toast.error(
                        'Please save the claim before adding attachments.',
                        {
                          className: 'toast-error',
                        },
                      );
                      return;
                    }
                    fileRef.current?.click();
                  }}
                  // disabled={!rowWatchValues?.srNo}
                  disabled={!expenseClaimHeaders?.claimNo}
                >
                  <img src={attachDocIcon} alt="Attach" />
                </button>
              ))}
            <button
              type="button"
              className="btn btn-link p-0"
              title="Click to download the attachment"
              onClick={handleFileView}
            >
              <Icon
                path={mdiEyeOutline}
                size={0.7}
                style={{
                  cursor:
                    attachmentExists ||
                    (rowWatchValues?.count && rowWatchValues?.count > 0)
                      ? 'pointer'
                      : 'not-allowed',
                  color:
                    attachmentExists ||
                    (rowWatchValues?.count && rowWatchValues?.count > 0)
                      ? '#079455'
                      : getClaimApprovalColor(expenseClaimHeaders?.claimStatus),
                  opacity:
                    attachmentExists ||
                    (rowWatchValues?.count && rowWatchValues?.count > 0)
                      ? 1
                      : 0.5,
                }}
              />
            </button>
          </div>
        </td>

        <td className="align-middle text-center">
          <div>
            {modeType === 'approve' && (
              <button
                type="button"
                className="btn btn-link p-0 me-2"
                title="Click to view past history"
                onClick={handlePastHistoryClick}
              >
                <Icon path={mdiFeatureSearchOutline} size={0.7} />
              </button>
            )}
            <button
              type="button"
              className="btn btn-link p-0 me-2"
              title="Click to enter or edit the additional details of the claim."
              ref={viewApproversRef}
              onClick={() => {
                if (!rowWatchValues?.expCategory) {
                  toast.error(
                    'Please fill in category and types before adding additional items.',
                    {
                      className: 'toast-error',
                    },
                  );
                  expCategoryRef.current?.focus();
                  expCategoryRef.current?.classList.add('highlight');

                  setTimeout(() => {
                    expCategoryRef.current?.classList.remove('highlight');
                  }, 7000);

                  return;
                }
                if (!rowWatchValues?.expType) {
                  toast.error(
                    'Please fill in types before adding additional items.',
                    {
                      className: 'toast-error',
                    },
                  );
                  expTypeRef.current?.focus();
                  expTypeRef.current?.classList.add('highlight');

                  setTimeout(() => {
                    expTypeRef.current?.focus();
                    expTypeRef.current?.classList.remove('highlight');
                  }, 7000);

                  return;
                }
                toggleModal();
              }}
            >
              <img
                src={ExpenseAddMoreIcon}
                alt="Additional Item"
                className="eec-row-icon"
              />
            </button>
            <button
              type="button"
              className="btn btn-link p-0 me-2"
              title="Click to view approvers for this claim item."
              onClick={handleExpenseApproverIconClick}
            >
              <img
                src={ExpenseApproverIcon}
                alt="Delete EEC"
                className="eec-row-icon"
              />
            </button>
            {formType !== CLAIM_FORM_TYPES.VIEW && (
              <button
                type="button"
                className="btn btn-link p-0"
                title="Click to delete this expense claim entry"
                onClick={() => {
                  open({
                    title: 'Delete Expense Claim Row',
                    message: 'Are you sure you want to delete this row?',
                    onConfirm: handleRowDelete,
                  });
                }}
              >
                <img
                  src={ExpenseDeleteRowIcon}
                  alt="Delete EEC"
                  className="eec-row-icon"
                />
              </button>
            )}
          </div>
        </td>
      </tr>
      <AdditionalDetailsModal
        isOpen={isAdditionalModalOpen}
        toggle={() => {
          if (viewApproversRef.current) {
            viewApproversRef.current.focus();
          }
          setIsAdditionalModalOpen(false);
        }}
        modelType={watch(`claims.${index}.expType`)}
        rowData={watch(`claims.${index}`)}
        onSubmit={null}
        rowIndex={index}
        formType={formType}
        checkLastRowAndAdd={checkLastRowAndAdd}
        handleAddRow={handleAddRow}
        expenseClaimHeaders={expenseClaimHeaders}
      />
      <input
        type="file"
        ref={fileRef}
        style={{ display: 'none' }}
        onChange={handleFileUpload}
        multiple
      />
      <ConfirmationModal />
    </>
  );
}

export default ExpenseClaimRow;
